
import { SpaceTimeClient } from '../spacetime/SpaceTimeClient';
import { EventHub } from '../events/EventHub';
import { INPUT_INTENT_EVENT } from '../controller/InputController';
import { GameState } from '../model/gamestate/GameState';
import { normalizeInputTypes, type Input, type InputType } from '../model/Input';
import { type SkillData } from '../spacetime/modules/SkillModule';
import { ActionTooltip } from './ActionTooltip';
import { getCodexPickerRequest } from './codexPickerConfig';
import { createMetaSkillPicker } from './MetaSkillPicker';
import { buildSkillSlotEl, showSkillTooltip } from './skillSlot';
import type { CodexPicker } from './CodexPicker';

export class SkillBar {
    private container: HTMLElement;
    private client: SpaceTimeClient;
    private eventHub: EventHub;
    private gameState: GameState;
    private codexPicker: CodexPicker;

    private knownSkills: SkillData[] = [];
    private skillIds: string[] = [];
    private readonly tooltip = new ActionTooltip();
    private metaPickerHandle: { destroy(): void; render(): void } | null = null;

    constructor(
        container: HTMLElement,
        client: SpaceTimeClient,
        eventHub: EventHub,
        gameState: GameState,
        codexPicker: CodexPicker
    ) {
        this.container = container;
        this.client = client;
        this.eventHub = eventHub;
        this.gameState = gameState;
        this.codexPicker = codexPicker;

        // Delegate right-click handling to this component
        this.container.addEventListener('contextmenu', this.onContextMenu.bind(this));
    }

    public updateSkills(skills: SkillData[]): void {
        this.knownSkills = skills;
        this.gameState.setKnownSkills(skills);
        this.render();
    }

    public updateSkillBar(skillIds: string[]): void {
        this.skillIds = skillIds;
        this.render();
    }

    public useSlot(barIndex: number): boolean {
        const skillId = this.skillIds[barIndex];
        if (!skillId) return false;

        const skill = this.knownSkills.find((s) => s.skillId === skillId);
        if (skill && skill.metaChildren.length > 0) {
            this.openMetaPicker(barIndex, skill);
            return true;
        }

        void this.handleSkillUse(skill);
        return true;
    }

    private async handleSkillUse(skill: SkillData): Promise<void> {
        console.log('[SkillBar] Using skill:', skill.skillId);
        const extraInputs: Input[] = [];
        let inputTypes: InputType[] | undefined;
        const codexPickerRequest = getCodexPickerRequest(skill);
        if (codexPickerRequest) {
            const selectedValue = await this.codexPicker.pick(codexPickerRequest.tab, {
                title: codexPickerRequest.title,
            });
            extraInputs.push({ kind: 'text', value: selectedValue ?? '' });
            inputTypes = normalizeInputTypes(skill.inputsJson);
        }
        this.eventHub.emit(INPUT_INTENT_EVENT, {
            type: 'useSkill',
            skillId: skill.skillId,
            inputTypes,
            inputs: extraInputs.length > 0 ? extraInputs : undefined,
        });
    }

    private openMetaPicker(barIndex: number, skill: SkillData): void {
        if (this.metaPickerHandle) {
            this.metaPickerHandle.destroy();
            this.metaPickerHandle = null;
        }

        const slotEl = this.container.children[barIndex] as HTMLElement | undefined;
        if (!slotEl) return;

        const subSkills = skill.metaChildren
            .map((id) => this.knownSkills.find((s) => s.skillId === id))
            .filter((s): s is SkillData => s !== undefined);

        if (subSkills.length === 0) return;

        this.metaPickerHandle = createMetaSkillPicker({
            anchorEl: slotEl,
            subSkills,
            onSelect: (selectedSkillId: string) => {
                this.metaPickerHandle = null;
                const selectedSkill = this.knownSkills.find((entry) => entry.skillId === selectedSkillId);
                if (selectedSkill) {
                    void this.handleSkillUse(selectedSkill);
                }
            },
            onDismiss: () => {
                this.metaPickerHandle = null;
            },
            getTick: () => this.gameState.getTick(),
            tooltip: this.tooltip,
        });
    }

    public render(): void {
        this.tooltip.hide();
        this.container.innerHTML = '';
        this.metaPickerHandle?.render();
        const currentTick = this.gameState.getTick();

        for (let i = 0; i < 9; i += 1) {
            const skillId = this.skillIds[i];
            const skill = skillId ? this.knownSkills.find((s) => s.skillId === skillId) : undefined;

            let slotEl: HTMLElement;
            if (skill) {
                ({ el: slotEl } = buildSkillSlotEl(skill, currentTick, i + 1, {
                    onClick: () => { void this.useSlot(i); },
                    onTooltipShow: (s, el) => { showSkillTooltip(this.tooltip, s, el); },
                    onTooltipHide: () => { this.tooltip.hide(); },
                }));
            } else {
                slotEl = document.createElement('div');
                slotEl.className = 'skill-bar-slot';
                const numberEl = document.createElement('span');
                numberEl.className = 'slot-number';
                numberEl.textContent = `${i + 1}`;
                slotEl.appendChild(numberEl);
            }

            this.attachDragHandlers(slotEl, i);
            this.container.appendChild(slotEl);
        }
    }

    private attachDragHandlers(slotEl: HTMLElement, index: number): void {
        slotEl.addEventListener('dragover', (e) => {
            if (e.dataTransfer?.types.includes('application/skill-id')) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                slotEl.classList.add('drag-over');
            }
        });

        slotEl.addEventListener('dragleave', () => {
            slotEl.classList.remove('drag-over');
        });

        slotEl.addEventListener('drop', (e) => {
            e.preventDefault();
            slotEl.classList.remove('drag-over');
            const droppedSkillId = e.dataTransfer?.getData('application/skill-id');
            if (droppedSkillId) {
                this.client.skills.addToSkillBarAt(droppedSkillId, index)
                    .catch((err) => console.error('[SkillBar] Failed to add skill at slot:', err));
            }
        });
    }

    private onContextMenu(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        if (!target) return;

        const slotEl = target.closest('.skill-bar-slot') as HTMLElement | null;
        if (!slotEl || !this.container.contains(slotEl)) return;

        // Only allow context menu actions if inventory/skills are "open" or similar? 
        // The original code hooked into PlayerInventory.onOpen/Close to attach this listener.
        // Ideally, we just check if the player is allowed to edit. For now, we preserve behavior
        // but the parent might want to toggle a `readonly` flag on this class.

        // Actually, looking at main.ts, it added the listener only when inventory was open.
        // For now, let's expose an enable/disable method or property if strict parity is needed,
        // or just allow it always (UX improvement?).
        // Let's stick to "always bind, but check condition" pattern if possible, 
        // or let the parent enable/disable. The original code was: 
        // playerInventory.onOpen(() => skillBarSlots.addEventListener...)
        // So we should probably expose `setEditable(boolean)` logic.

        // For this refactor, I'll let it execute but we should coordinate with PlayerInventory state.
        // Since we don't have access to PlayerInventory state here easily without coupling,
        // I will expose `setEditable(boolean)` and let main.ts drive it.
        if (!this.isEditable) return;

        const slotIndex = Array.from(this.container.children).indexOf(slotEl);
        if (slotIndex < 0) return;

        const skillId = this.skillIds[slotIndex];
        if (!skillId) return;

        event.preventDefault();
        this.client.skills.removeFromSkillBar(skillId)
            .catch((err) => console.error('[SkillBar] Failed to remove skill:', err));
    }

    private isEditable = false;
    public setEditable(editable: boolean): void {
        this.isEditable = editable;
    }
}
